import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApprovalCreate,
  ApprovalRequest,
  Attachment,
  CaseCreate,
  CaseEvent,
  CommitmentSchema,
  MilestoneSchema,
  Note,
  NoteCreate,
  Notification,
  OnboardingInstance,
  OnboardingTemplateSchema,
  SupportCase,
  Task,
  TaskCreate,
  TaskUpdate,
  TimelineEntry,
} from '@hospasense/contracts';
import type { z } from 'zod';
import { apiGet, apiSend } from './api.js';

type Milestone = z.infer<typeof MilestoneSchema>;
type Commitment = z.infer<typeof CommitmentSchema>;
type OnboardingTemplate = z.infer<typeof OnboardingTemplateSchema>;

/* ------------------------------------------------------------- onboarding */
export function useOnboardingPipeline(enabled: boolean) {
  return useQuery<{ items: OnboardingInstance[] }>({
    queryKey: ['onboarding'],
    queryFn: () => apiGet('/api/v1/onboarding'),
    enabled,
  });
}

export function useOrgOnboarding(orgId: string | undefined, enabled: boolean) {
  return useQuery<OnboardingInstance>({
    queryKey: ['onboarding', 'org', orgId],
    queryFn: () => apiGet(`/api/v1/organisations/${orgId}/onboarding`),
    enabled: Boolean(orgId) && enabled,
    retry: false,
  });
}

export function useOnboardingTemplates(enabled: boolean) {
  return useQuery<{ items: OnboardingTemplate[] }>({
    queryKey: ['onboarding', 'templates'],
    queryFn: () => apiGet('/api/v1/onboarding/templates'),
    enabled,
  });
}

export function useInvalidateOnboarding() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['onboarding'] });
}

export function useStartOnboarding(orgId: string) {
  const invalidate = useInvalidateOnboarding();
  return useMutation({
    mutationFn: (input: { templateId: string; targetGoLiveDate?: string }) =>
      apiSend('POST', `/api/v1/organisations/${orgId}/onboarding`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateStep() {
  const invalidate = useInvalidateOnboarding();
  return useMutation({
    mutationFn: (input: { stepId: string; body: Record<string, unknown> }) =>
      apiSend('POST', `/api/v1/onboarding/steps/${input.stepId}`, input.body),
    onSuccess: invalidate,
  });
}

export function useAddEvidence() {
  const invalidate = useInvalidateOnboarding();
  return useMutation({
    mutationFn: (input: {
      stepId: string;
      kind: 'link' | 'note';
      title: string;
      content: string;
    }) =>
      apiSend('POST', `/api/v1/onboarding/steps/${input.stepId}/evidence`, {
        kind: input.kind,
        title: input.title,
        content: input.content,
      }),
    onSuccess: invalidate,
  });
}

/* -------------------------------------------------------------- workspace */
export function useNotes(orgId: string | undefined, enabled: boolean) {
  return useQuery<{ items: Note[] }>({
    queryKey: ['notes', orgId],
    queryFn: () => apiGet(`/api/v1/organisations/${orgId}/notes`),
    enabled: Boolean(orgId) && enabled,
  });
}

export function useCreateNote(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NoteCreate) =>
      apiSend('POST', `/api/v1/organisations/${orgId}/notes`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', orgId] }),
  });
}

export function useTasks(orgId: string | undefined, enabled: boolean) {
  return useQuery<{ items: Task[] }>({
    queryKey: ['tasks', orgId],
    queryFn: () => apiGet(`/api/v1/organisations/${orgId}/tasks`),
    enabled: Boolean(orgId) && enabled,
  });
}

export function useCreateTask(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaskCreate) =>
      apiSend('POST', `/api/v1/organisations/${orgId}/tasks`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', orgId] }),
  });
}

export function useUpdateTask(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; body: TaskUpdate }) =>
      apiSend('PATCH', `/api/v1/tasks/${input.taskId}`, input.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', orgId] }),
  });
}

export function useBulkTaskStatus(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskIds: string[]; status: 'done' | 'cancelled' | 'in_progress' }) =>
      apiSend<{ results: { id: string; ok: boolean; error?: string }[] }>(
        'POST',
        '/api/v1/tasks/bulk-status',
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', orgId] }),
  });
}

export function useMilestones(orgId: string | undefined, enabled: boolean) {
  return useQuery<{ items: Milestone[] }>({
    queryKey: ['milestones', orgId],
    queryFn: () => apiGet(`/api/v1/organisations/${orgId}/milestones`),
    enabled: Boolean(orgId) && enabled,
  });
}

export function useCommitments(orgId: string | undefined) {
  return useQuery<{ items: Commitment[] }>({
    queryKey: ['commitments', orgId],
    queryFn: () => apiGet(`/api/v1/organisations/${orgId}/commitments`),
    enabled: Boolean(orgId),
  });
}

export function useTimeline(orgId: string | undefined) {
  return useQuery<{ items: TimelineEntry[] }>({
    queryKey: ['timeline', orgId],
    queryFn: () => apiGet(`/api/v1/organisations/${orgId}/timeline`),
    enabled: Boolean(orgId),
  });
}

/* ---------------------------------------------------------------- support */
export function useCases(params: URLSearchParams, enabled: boolean) {
  const qs = params.toString();
  return useQuery<{
    items: SupportCase[];
    meta: { totalItems: number; totalPages: number; page: number };
  }>({
    queryKey: ['cases', qs],
    queryFn: () => apiGet(`/api/v1/cases${qs ? `?${qs}` : ''}`),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useCaseDetail(caseId: string | undefined) {
  return useQuery<{ case: SupportCase; events: CaseEvent[]; attachments: Attachment[] }>({
    queryKey: ['case', caseId],
    queryFn: () => apiGet(`/api/v1/cases/${caseId}`),
    enabled: Boolean(caseId),
  });
}

export function useCaseMutation(caseId: string | undefined) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['cases'] });
    if (caseId) void qc.invalidateQueries({ queryKey: ['case', caseId] });
  };
  return {
    create: useMutation({
      mutationFn: (input: CaseCreate) => apiSend<{ id: string }>('POST', '/api/v1/cases', input),
      onSuccess: invalidate,
    }),
    transition: useMutation({
      mutationFn: (input: { toStatus: string; note: string; expectedVersion: number }) =>
        apiSend('POST', `/api/v1/cases/${caseId}/transition`, input),
      onSuccess: invalidate,
    }),
    comment: useMutation({
      mutationFn: (input: { body: string }) =>
        apiSend('POST', `/api/v1/cases/${caseId}/comment`, input),
      onSuccess: invalidate,
    }),
    communication: useMutation({
      mutationFn: (input: { direction: string; channel: string; summary: string }) =>
        apiSend('POST', `/api/v1/cases/${caseId}/communication`, input),
      onSuccess: invalidate,
    }),
    attach: useMutation({
      mutationFn: (input: { filename: string; contentType: string; dataBase64: string }) =>
        apiSend('POST', `/api/v1/cases/${caseId}/attachments`, input),
      onSuccess: invalidate,
    }),
  };
}

/* -------------------------------------------------------------- approvals */
export function useApprovals(params: URLSearchParams, enabled: boolean) {
  const qs = params.toString();
  return useQuery<{ items: ApprovalRequest[] }>({
    queryKey: ['approvals', qs],
    queryFn: () => apiGet(`/api/v1/approvals${qs ? `?${qs}` : ''}`),
    enabled,
  });
}

export function useApprovalMutation() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['approvals'] });
    void qc.invalidateQueries({ queryKey: ['onboarding'] });
  };
  return {
    create: useMutation({
      mutationFn: (input: ApprovalCreate) =>
        apiSend<{ id: string }>('POST', '/api/v1/approvals', input),
      onSuccess: invalidate,
    }),
    decide: useMutation({
      mutationFn: (input: {
        id: string;
        decision: 'approve' | 'reject';
        note: string;
        expectedVersion: number;
      }) =>
        apiSend('POST', `/api/v1/approvals/${input.id}/decision`, {
          decision: input.decision,
          note: input.note,
          expectedVersion: input.expectedVersion,
        }),
      onSuccess: invalidate,
    }),
    execute: useMutation({
      mutationFn: (input: { id: string; expectedVersion: number }) =>
        apiSend<{ result: string }>('POST', `/api/v1/approvals/${input.id}/execute`, {
          expectedVersion: input.expectedVersion,
        }),
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: (input: { id: string; expectedVersion: number }) =>
        apiSend('POST', `/api/v1/approvals/${input.id}/cancel`, {
          expectedVersion: input.expectedVersion,
        }),
      onSuccess: invalidate,
    }),
  };
}

/* ----------------------------------------------------------- notifications */
export function useNotifications() {
  return useQuery<{ items: Notification[]; unreadCount: number }>({
    queryKey: ['notifications'],
    queryFn: () => apiGet('/api/v1/notifications'),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return {
    one: useMutation({
      mutationFn: (id: string) => apiSend('POST', `/api/v1/notifications/${id}/read`),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
    }),
    all: useMutation({
      mutationFn: () => apiSend('POST', '/api/v1/notifications/read-all'),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
    }),
  };
}
